import { useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { CloseIcon, SearchIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import { MESSAGE } from "@/constants/message";
import useOnClickOutside from "@/hooks/ui/useClickOutside";

import { twMerge } from "tailwind-merge";

// Render values in windows (infinite scroll) so large attributes stay snappy —
// same approach the attribute filter uses.
const CONTENT_LIMIT = 100;
const SCROLLABLE_TARGET_ID = "edit-category-scroll";

interface EditCategoryPanelProps {
  /** Committed category name (the "Category" input). */
  categoryName: string;
  /** Committed values assigned to this category (the "Assigned Values" list). */
  assignedValues: string[];
  /** All unique values of the selected attribute (the selectable options). */
  attributeValues: string[];
  /** Values already assigned to other categories — shown disabled/locked. */
  valuesInOtherCategories: string[];
  /** Whether the distinct attribute values are still loading from the API. */
  isLoadingValues?: boolean;
  /** Commit the edits (category name + assigned values). */
  onSave: (categoryName: string, assignedValues: string[]) => void;
  onClose: () => void;
}

interface ValuesListProps {
  matchedValues: string[];
  lockedValuesSet: Set<string>;
  draftAssignedValuesSet: Set<string>;
  toggleValue: (value: string, checked: boolean) => void;
}

const sameValues = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

// Owns its own `renderLimit` state. The parent remounts this component (via a
// `key` that changes whenever the value set or search changes), which resets
// `renderLimit` back to CONTENT_LIMIT for free — no ref/effect needed to
// "watch" for that change.
const ValuesList = ({
  matchedValues,
  lockedValuesSet,
  draftAssignedValuesSet,
  toggleValue
}: ValuesListProps) => {
  const [renderLimit, setRenderLimit] = useState(CONTENT_LIMIT);
  const visibleValues = matchedValues.slice(0, renderLimit);
  const hasMore = renderLimit < matchedValues.length;

  const loadMore = () => setRenderLimit((prev) => prev + CONTENT_LIMIT);

  return (
    <InfiniteScroll
      dataLength={visibleValues.length}
      next={loadMore}
      hasMore={hasMore}
      loader={null}
      scrollableTarget={SCROLLABLE_TARGET_ID}
      scrollThreshold="95%"
      className="flex flex-col gap-2"
    >
      {visibleValues.map((value) => {
        const isLocked = lockedValuesSet.has(value);
        return (
          <Checkbox
            key={value}
            // `id` so clicking the label text toggles the checkbox.
            id={`edit-category-${value}`}
            // Locked values (assigned to another category) show a
            // check but are disabled, so users see they're taken.
            checked={draftAssignedValuesSet.has(value) || isLocked}
            disabled={isLocked}
            onChange={(checked) => toggleValue(value, checked)}
            label={value}
            inputClassName="size-4"
            data-testid={`edit-category-value-${value}`}
          />
        );
      })}
    </InfiniteScroll>
  );
};

const EditCategoryPanel = ({
  categoryName,
  assignedValues,
  attributeValues,
  valuesInOtherCategories,
  isLoadingValues = false,
  onSave,
  onClose
}: EditCategoryPanelProps) => {
  const panelRef = useRef(null);
  useOnClickOutside(panelRef, onClose, []);

  const [searchQuery, setSearchQuery] = useState("");

  // Pending edits — not applied to the category until Save is clicked.
  const [draftCategoryName, setDraftCategoryName] = useState(categoryName);
  const [draftAssignedValues, setDraftAssignedValues] =
    useState<string[]>(assignedValues);

  const lockedValuesSet = useMemo(
    () => new Set(valuesInOtherCategories),
    [valuesInOtherCategories]
  );
  const draftAssignedValuesSet = useMemo(
    () => new Set(draftAssignedValues),
    [draftAssignedValues]
  );

  const matchedValues = useMemo(
    () =>
      attributeValues.filter((v) =>
        v.toLowerCase().includes(searchQuery.trim().toLowerCase())
      ),
    [attributeValues, searchQuery]
  );

  // Matched values that can still be selected here (not owned by another category).
  const selectableValues = matchedValues.filter((v) => !lockedValuesSet.has(v));
  const allSelectableSelected =
    selectableValues.length > 0 &&
    selectableValues.every((v) => draftAssignedValuesSet.has(v));

  // Counter mirrors the attribute filter: "x of y" over the current search
  // results — y is how many values are visible, x how many of those are selected.
  const selectedVisibleCount = matchedValues.filter((v) =>
    draftAssignedValuesSet.has(v)
  ).length;

  const isDirty =
    draftCategoryName.trim() !== categoryName ||
    !sameValues(draftAssignedValues, assignedValues);
  // PRD: a category must have a name (values are optional — empty steps are
  // allowed). Save is enabled only when there is a change and the name is set.
  const canSave = isDirty && draftCategoryName.trim().length > 0;

  const toggleValue = (value: string, checked: boolean) => {
    setDraftAssignedValues((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setDraftAssignedValues((prev) => {
      // Select All applies to the current view only: checking it makes the
      // selection exactly the visible (search-filtered) values, dropping any
      // earlier selection made without a search. With no search active this is
      // the full list, so the behavior is unchanged. Unchecking removes just
      // the visible values.
      if (checked) return [...selectableValues];
      const remove = new Set(selectableValues);
      return prev.filter((v) => !remove.has(v));
    });
  };

  const handleSave = () => {
    onSave(draftCategoryName.trim() || categoryName, draftAssignedValues);
    onClose();
  };

  return (
    <div
      ref={panelRef}
      data-testid="edit-category-panel"
      className={twMerge(
        "z-[99] font-sans border border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col py-[16px] fixed left-[430px] top-[30px] text-xs"
      )}
    >
      {/* Header */}
      <div className="flex flex-row items-start justify-between px-4">
        <h2 className="text-txtHeading text-[20px] font-bold">
          {MESSAGE.LEGEND_EDIT_CATEGORY}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close edit category"
          data-testid="edit-category-close"
          className="border-0 bg-transparent p-0"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>

      <div className="mt-3 flex flex-col gap-3 px-4">
        {/* Rename */}
        <div className="flex flex-col gap-1">
          <span className="text-grey text-xs">Category</span>
          <input
            type="text"
            value={draftCategoryName}
            onChange={(e) => setDraftCategoryName(e.currentTarget.value)}
            className="w-full rounded-lg border border-gainsboro px-3 py-2 text-xs text-txtHeading outline-none focus:border-primary-teal"
            data-testid="edit-category-name"
          />
          <span className="text-[10px] text-grey">
            {MESSAGE.LEGEND_RENAME_CATEGORY}
          </span>
        </div>

        {/* Assigned values */}
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-txtHeading text-sm font-semibold">
              {MESSAGE.LEGEND_ASSIGNED_VALUES}
            </p>
            <p className="text-[10px] text-grey">
              {MESSAGE.LEGEND_ONE_MORE_VALUES}
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-gainsboro px-2 py-1.5">
            <SearchIcon className="size-3.5 fill-grey" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              placeholder="Search"
              className="w-full text-xs text-txtHeading outline-none"
              data-testid="edit-category-search"
            />
          </div>

          <div className="flex items-center justify-between">
            <span
              className="text-[10px] text-grey"
              data-testid="edit-category-selected-count"
            >
              Selected options ({selectedVisibleCount} of {matchedValues.length}
              )
            </span>
            <button
              type="button"
              onClick={() => setDraftAssignedValues([])}
              disabled={draftAssignedValues.length === 0}
              className="text-[10px] text-primary-teal disabled:opacity-50"
              data-testid="edit-category-reset"
            >
              {MESSAGE.LEGEND_RESET}
            </button>
          </div>

          <div
            id={SCROLLABLE_TARGET_ID}
            className="flex max-h-60 flex-col gap-2 overflow-y-auto custom-scrollbar pr-1"
          >
            {isLoadingValues && (
              <p
                className="py-4 text-center text-[12px] text-grey"
                data-testid="edit-category-loading"
              >
                Loading values…
              </p>
            )}
            {!isLoadingValues && selectableValues.length > 0 && (
              <Checkbox
                id="edit-category-select-all"
                checked={allSelectableSelected}
                onChange={toggleSelectAll}
                label="Select All"
                inputClassName="size-4"
                data-testid="edit-category-select-all"
              />
            )}
            {!isLoadingValues && matchedValues.length === 0 && (
              <div className="flex min-h-[240px] flex-col items-center justify-center">
                <SearchIcon className="size-6 fill-grey pb-1" />
                <p className="text-[16px] text-txtBody">
                  {MESSAGE.LEGEND_NO_VALUES_FOUND_TITLE}
                </p>
                <p className="py-2 text-center text-[12px] text-grey">
                  {MESSAGE.LEGEND_NO_VALUES_FOUND_DESCRIPTION}
                </p>
              </div>
            )}
            {!isLoadingValues && matchedValues.length > 0 && (
              <ValuesList
                key={`${searchQuery}::${attributeValues.length}`}
                matchedValues={matchedValues}
                lockedValuesSet={lockedValuesSet}
                draftAssignedValuesSet={draftAssignedValuesSet}
                toggleValue={toggleValue}
              />
            )}
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          className="h-8 rounded-lg text-xs"
          data-testid="edit-category-save"
        >
          {MESSAGE.LEGEND_SAVE}
        </Button>
      </div>
    </div>
  );
};

export default EditCategoryPanel;
